////////////////////////////////////////
// # GOLDEN RULE!!!! HONOR ABOVE ALL!!!!
////////////////////////////////////////
// NO NEW FILES!!!!!!!!!
////////////////////////////////////////

/**
 * ════════════════════════════════════════════════════════════════════════════════
 * REGISTER POD - CUSTOMER REGISTRATION ORCHESTRATOR (POD/PEA PATTERN)
 * ════════════════════════════════════════════════════════════════════════════════
 *
 * ARCHITECTURAL ROLE: Orchestrates customer onboarding flow including validation,
 * API registration, password hashing delegation, and Account Knight navigation
 *
 * I[R{DVT}]O PATTERN:
 *   INPUT: Registration form data (email, password, company, personal info, terms)
 *   REQUIREMENTS: Multi-field validation, password strength, duplicate detection
 *   OUTPUT: Created customer with JWT token, redirect to Account Knight
 *
 * SECURITY: Password transmitted to backend for bcrypt hashing (10 rounds minimum)
 * GDPR: Terms acceptance creates lawful basis for processing (Article 6(1)(a))
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Pea_Lobby_Register_SRC_Auth_Customer_Kings from '../pea/Bitsbound_Kings_Customer_Auth_SRC_Register_Lobby_Pea.js';

/**
 * ════════════════════════════════════════════════════════════════════════════════
 * GA4 REGISTRATION FORM ANALYTICS - T_GA4_FORM_TRACKING_2026_01_02
 * ════════════════════════════════════════════════════════════════════════════════
 *
 * TRACKING EVENTS:
 *   - registration_form_view: When form is first displayed
 *   - registration_field_focus: When user starts filling a field (first time only)
 *   - registration_form_progress: Periodic progress updates (% fields completed)
 *   - registration_validation_error: When validation fails with specific errors
 *   - registration_form_submit: When form is submitted (success/failure tracked)
 *   - registration_form_abandon: When user leaves form without submitting
 *
 * FUNNEL ANALYSIS: These events enable GA4 funnel visualization:
 *   form_view → first_field → 50%_progress → 100%_progress → submit → success
 */

// D_GA4_HELPER: Type-safe gtag wrapper
const trackGA4Event = (eventName: string, params: Record<string, string | number | boolean>) => {
  if (typeof window !== 'undefined' && typeof (window as any).gtag === 'function') {
    (window as any).gtag('event', eventName, params);
    console.log(`📊 GA4: ${eventName}`, params);
  }
};

/**
 * DEFINITION (D): RegisterFormData interface for comprehensive registration
 * Captures all required information for B2B customer onboarding
 */
interface RegisterFormData {
  email: string;               // DEFINITION: Primary contact email (becomes username)
  password: string;            // DEFINITION: User-chosen password (min 8 characters)
  confirmPassword: string;     // DEFINITION: Password confirmation for typo prevention
  companyName: string;         // DEFINITION: Legal entity name for B2B relationship
  firstName: string;           // DEFINITION: Contact first name for personalization
  lastName: string;            // DEFINITION: Contact last name for correspondence
  acceptTerms: boolean;        // DEFINITION: Terms of Service acceptance (required, GDPR Article 6)
  subscribeNewsletter: boolean;// DEFINITION: Marketing consent (optional, GDPR Article 7)
}

/**
 * TRANSFORMATION (T): Register Pod component orchestrates registration logic
 */
const Pod_Lobby_Register_SRC_Auth_Customer_Kings: React.FC = () => {
  const navigate = useNavigate();  // DEFINITION: Navigation hook
  const [searchParams] = useSearchParams();  // DEFINITION: URL query params

  // DEFINITION: Registration form state with all required fields
  const [formData, setFormData] = useState<RegisterFormData>({
    email: '',                    // INPUT: User email
    password: '',                 // INPUT: User password
    confirmPassword: '',          // INPUT: Password confirmation
    companyName: '',              // INPUT: Company legal name
    firstName: '',                // INPUT: Contact first name
    lastName: '',                 // INPUT: Contact last name
    acceptTerms: false,           // INPUT: ToS acceptance (required)
    subscribeNewsletter: false    // INPUT: Marketing consent (optional)
  });

  const [errors, setErrors] = useState<Record<string, string>>({});  // DEFINITION: Field errors
  const [isLoading, setIsLoading] = useState(false);                  // DEFINITION: Loading state
  const [verificationPending, setVerificationPending] = useState(false);  // DEFINITION: Email verification required
  const [registeredEmail, setRegisteredEmail] = useState('');  // DEFINITION: Email that needs verification

  // ════════════════════════════════════════════════════════════════════════════════
  // T_GA4_FORM_TRACKING_2026_01_02: Form analytics state
  // ════════════════════════════════════════════════════════════════════════════════
  const touchedFieldsRef = useRef<Set<string>>(new Set());  // D_TOUCHED_FIELDS: Track which fields user has interacted with
  const formStartTimeRef = useRef<number>(Date.now());       // D_FORM_START: Track when user started filling form
  const hasTrackedViewRef = useRef<boolean>(false);          // D_VIEW_TRACKED: Prevent duplicate view events
  const lastProgressRef = useRef<number>(0);                 // D_LAST_PROGRESS: Track last reported progress percentage
  const hasSubmittedRef = useRef<boolean>(false);            // D_SUBMITTED: Track if form was submitted (for abandon detection)

  // T_GA4_FORM_VIEW: Track form view on component mount
  useEffect(() => {
    if (!hasTrackedViewRef.current) {
      hasTrackedViewRef.current = true;
      formStartTimeRef.current = Date.now();
      trackGA4Event('registration_form_view', {
        form_name: 'customer_registration',
        page_location: window.location.href
      });
    }
  }, []);

  // T_GA4_FORM_ABANDON: Track form abandonment on unmount (only if not submitted)
  useEffect(() => {
    return () => {
      if (!hasSubmittedRef.current && touchedFieldsRef.current.size > 0) {
        const timeSpentSeconds = Math.round((Date.now() - formStartTimeRef.current) / 1000);
        const progress = calculateFormProgress();
        trackGA4Event('registration_form_abandon', {
          form_name: 'customer_registration',
          fields_touched: touchedFieldsRef.current.size,
          last_field: Array.from(touchedFieldsRef.current).pop() || 'none',
          progress_percent: progress,
          time_spent_seconds: timeSpentSeconds
        });
      }
    };
  }, []);

  // D_PROGRESS_CALCULATOR: Calculate form completion percentage
  const calculateFormProgress = useCallback((): number => {
    const requiredFields = ['firstName', 'lastName', 'email', 'password', 'confirmPassword', 'acceptTerms'];
    let filledCount = 0;

    if (formData.firstName) filledCount++;
    if (formData.lastName) filledCount++;
    if (formData.email) filledCount++;
    if (formData.password) filledCount++;
    if (formData.confirmPassword) filledCount++;
    if (formData.acceptTerms) filledCount++;

    return Math.round((filledCount / requiredFields.length) * 100);
  }, [formData]);

  // T_GA4_PROGRESS_TRACKING: Track form progress at 25%, 50%, 75%, 100%
  useEffect(() => {
    const progress = calculateFormProgress();
    const milestones = [25, 50, 75, 100];

    for (const milestone of milestones) {
      if (progress >= milestone && lastProgressRef.current < milestone) {
        trackGA4Event('registration_form_progress', {
          form_name: 'customer_registration',
          progress_percent: milestone,
          fields_completed: touchedFieldsRef.current.size
        });
        lastProgressRef.current = milestone;
        break; // Only track one milestone at a time
      }
    }
  }, [formData, calculateFormProgress]);

  // T_OAUTH_NO_ACCOUNT_2025_12_30: Detect when user tried OAuth without existing account
  // Display friendly message and pre-fill email if provided
  useEffect(() => {
    const error = searchParams.get('error');
    const email = searchParams.get('email');

    if (error === 'oauth_no_account') {
      // TRANSFORMATION: Show message explaining they need to register first
      setErrors({
        submit: 'No account found with that Google email. Please register with email and password first, then you can use Google to sign in.'
      });

      // TRANSFORMATION: Pre-fill email if provided from OAuth attempt
      if (email) {
        setFormData(prev => ({ ...prev, email: decodeURIComponent(email) }));
      }
    }
  }, [searchParams]);

  /**
   * VALIDATION (V): Comprehensive registration form validation
   * Validates all required fields, email format, password strength, and terms acceptance
   *
   * INPUT: Current formData state
   * OUTPUT: Boolean (true if valid) and updates errors state
   */
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // VALIDATION: First name required
    if (!formData.firstName) {
      newErrors.firstName = 'First name is required';
    }

    // VALIDATION: Last name required
    if (!formData.lastName) {
      newErrors.lastName = 'Last name is required';
    }

    // VALIDATION: Email required and format check
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    // T_OPTIONAL_COMPANY_2026_01_02: Company name is now optional
    // Solo attorneys and individuals may not have a company name

    // VALIDATION: Password required and strength check (NIST 800-63B minimum)
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    // VALIDATION: Password confirmation required and match check
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // VALIDATION: Terms acceptance required (GDPR Article 6 lawful basis)
    if (!formData.acceptTerms) {
      newErrors.acceptTerms = 'You must agree to the terms';
    }

    setErrors(newErrors);  // TRANSFORMATION: Update error state

    // T_GA4_VALIDATION_ERRORS: Track validation failures with specific error details
    const errorCount = Object.keys(newErrors).length;
    if (errorCount > 0) {
      trackGA4Event('registration_validation_error', {
        form_name: 'customer_registration',
        error_count: errorCount,
        error_fields: Object.keys(newErrors).join(','),
        progress_percent: calculateFormProgress()
      });
    }

    return errorCount === 0;  // OUTPUT: Validation result
  };
  
  /**
   * TRANSFORMATION (T): Handles registration form submission and customer creation
   * Orchestrates validation, API call, token storage, and navigation
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();  // VALIDATION: Prevent default form submission

    // T_GA4_SUBMIT_ATTEMPT: Track form submission attempt
    const timeToSubmitSeconds = Math.round((Date.now() - formStartTimeRef.current) / 1000);
    trackGA4Event('registration_form_submit_attempt', {
      form_name: 'customer_registration',
      fields_touched: touchedFieldsRef.current.size,
      time_to_submit_seconds: timeToSubmitSeconds,
      progress_percent: calculateFormProgress()
    });

    // VALIDATION: Validate all fields before API call
    if (!validateForm()) {
      return;  // OUTPUT: Exit early if validation fails
    }

    // T_GA4_MARK_SUBMITTED: Mark as submitted to prevent abandon tracking
    hasSubmittedRef.current = true;

    setIsLoading(true);
    
    try {
      // TRANSFORMATION: Environment-aware auth backend URL
      // VALIDATION: Check for production hostnames (main domain OR direct Vercel app access)
      const hostname = window.location.hostname;
      const isProduction = hostname === 'www.bitsbound.com' ||
                          hostname === 'bitsbound.com' ||
                          hostname.includes('vercel.app');
      const authBackendUrl = isProduction
        ? 'https://bitsbound-auth-backend.vercel.app'  // DEFINITION: Production auth backend
        : 'http://localhost:2102';                      // DEFINITION: Local development (port 2102)
      console.log('Sending registration request to:', `${authBackendUrl}/api/auth/register`);
      const response = await fetch(`${authBackendUrl}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          companyName: formData.companyName,
          password: formData.password,
          confirmPassword: formData.confirmPassword,
          acceptTerms: formData.acceptTerms,  // LEGAL: ToS + Privacy + AI Disclaimer acceptance
          subscribeNewsletter: formData.subscribeNewsletter
        })
      });
      
      const data = await response.json();

      console.log('Registration response:', {
        ok: response.ok,
        status: response.status,
        data
      });

      console.log('🔍 DEBUG: Full response data structure:', JSON.stringify(data, null, 2));
      console.log('🔍 DEBUG: data.customerId =', data.customerId);
      console.log('🔍 DEBUG: data.customer =', data.customer);
      console.log('🔍 DEBUG: data.customer?.id =', data.customer?.id);

      // V_EMAIL_VERIFICATION: Check if email verification is required (new registration flow)
      if ((response.status === 200 || response.status === 201) && data.success && data.requiresVerification) {
        console.log('📧 Email verification required for:', data.customer?.email || formData.email);

        // 📊 GOOGLE ADS CONVERSION TRACKING: Fire signupComplete on successful registration
        // This is the PRIMARY conversion tracking location since ALL new registrations require verification
        // Conversion fires when user successfully submits registration form (lead form submission)
        if (typeof window !== 'undefined' && (window as any).bbEvents?.signupComplete) {
          (window as any).bbEvents.signupComplete();
          console.log('📊 Google Ads: signupComplete conversion tracked (verification pending)');
        }

        // 📊 GA4 TRACKING: Track signup start for funnel analysis
        if (typeof window !== 'undefined' && typeof (window as any).gtag === 'function') {
          (window as any).gtag('event', 'sign_up', {
            method: 'email',
            status: 'pending_verification'
          });
          console.log('📊 GA4: sign_up event tracked (pending verification)');
        }

        // T_GA4_SUBMIT_SUCCESS: Track successful form submission
        trackGA4Event('registration_form_submit_result', {
          form_name: 'customer_registration',
          success: true,
          requires_verification: true,
          time_to_submit_seconds: Math.round((Date.now() - formStartTimeRef.current) / 1000)
        });

        setVerificationPending(true);
        setRegisteredEmail(data.customer?.email || formData.email);
        setIsLoading(false);
        return; // Exit and show verification pending UI
      }

      // Check if we got a successful response (either 200 for existing user or 201 for new) WITH token
      if ((response.status === 200 || response.status === 201) && data.success && data.token) {
        console.log('Registration/Login successful, storing credentials...');

        // VALIDATION: Extract customerId from correct location (Auth backend returns customer.id, NOT top-level customerId)
        const actualCustomerId = data.customerId || data.customer?.id;

        if (!actualCustomerId) {
          console.error('❌ CRITICAL: No customerId in auth response!', data);
          setErrors({ submit: 'Registration succeeded but customer ID missing. Please contact support.' });
          return;
        }

        console.log('✅ Extracted customerId:', actualCustomerId);

        // 📊 GA4 FUNNEL TRACKING: Sign-up event + user_id for cross-device tracking
        if (typeof window !== 'undefined' && typeof (window as any).gtag === 'function') {
          // Track sign_up event (GA4 recommended event)
          (window as any).gtag('event', 'sign_up', {
            method: 'email'
          });
          // Set user_id for cross-device/cross-session tracking
          (window as any).gtag('config', 'G-1LSNGFSBEF', {
            user_id: actualCustomerId
          });
          console.log('📊 GA4: sign_up event tracked for user:', actualCustomerId);
        }

        // 📊 GOOGLE ADS CONVERSION TRACKING: Fire signupComplete conversion event
        // This triggers the Google Ads conversion with label DrRNCK_vytgbEIWps7lC
        if (typeof window !== 'undefined' && (window as any).bbEvents?.signupComplete) {
          (window as any).bbEvents.signupComplete();
          console.log('📊 Google Ads: signupComplete conversion tracked');
        }

        // 🔥 CRITICAL FIX (2025-10-14): Clear ALL localStorage before setting new values
        // This prevents stale data from previous sessions causing identity confusion
        localStorage.clear(); // 🧹 Clear all localStorage entries
        console.log('🧹 Cleared all localStorage entries before new registration');

        // Store JWT token and customer info in THIS domain's localStorage
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('customerId', actualCustomerId);

        // Store company name if available
        if (data.customer?.companyName) {
          localStorage.setItem('companyName', data.customer.companyName);
        }

        // Store normalized company name for URL routing
        if (data.customer?.normalizedCompanyName) {
          localStorage.setItem('normalizedCompanyName', data.customer.normalizedCompanyName);
        }

        console.log('✅ New localStorage values set:', {
          authToken: '(token hidden)',
          customerId: actualCustomerId,
          companyName: data.customer?.companyName,
          normalizedCompanyName: data.customer?.normalizedCompanyName
        });

        // TRANSFORMATION: Environment-aware redirect to Account dashboard
        // VALIDATION: Check for production hostnames (main domain OR direct Vercel app access)
        const redirectHostname = window.location.hostname;
        const isProductionRedirect = redirectHostname === 'www.bitsbound.com' ||
                                     redirectHostname === 'bitsbound.com' ||
                                     redirectHostname.includes('vercel.app');

        // Build redirect URL with auth data as query parameters
        const redirectParams = new URLSearchParams({
          token: data.token,
          customerId: actualCustomerId,
          companyName: data.customer?.companyName || '',
          email: data.customer?.email || formData.email
        });

        let redirectPath: string;
        if (isProductionRedirect) {
          // PRODUCTION: Route through main domain proxy (bitsbound.com/_customer-account/)
          // This keeps users on bitsbound.com instead of exposing internal Vercel URLs
          redirectPath = `https://www.bitsbound.com/_customer-account/?${redirectParams.toString()}`;
        } else {
          // LOCAL DEV: Use port 2103 for Account Knight
          const currentHost = window.location.hostname; // 'localhost' or '127.0.0.1'
          redirectPath = `http://${currentHost}:2103?${redirectParams.toString()}`;
        }

        console.log('Redirecting to Account dashboard:', redirectPath);

        // Use window.location.replace for immediate navigation
        window.location.replace(redirectPath);
        // Prevent any further execution
        return;
      } else {
        console.error('Registration failed:', data.error || 'Unknown error');
        // T_GA4_SUBMIT_FAILURE: Track registration failure
        trackGA4Event('registration_form_submit_result', {
          form_name: 'customer_registration',
          success: false,
          error_type: 'api_error',
          error_message: data.error || 'Unknown error',
          time_to_submit_seconds: Math.round((Date.now() - formStartTimeRef.current) / 1000)
        });
        hasSubmittedRef.current = false; // Allow abandon tracking since submission failed
        setErrors({ submit: data.error || 'Registration failed. Please try again.' });
      }
    } catch (error) {
      console.error('Registration network/fetch error:', error);
      // T_GA4_SUBMIT_NETWORK_ERROR: Track network/fetch errors
      trackGA4Event('registration_form_submit_result', {
        form_name: 'customer_registration',
        success: false,
        error_type: 'network_error',
        error_message: error instanceof Error ? error.message : 'Network error',
        time_to_submit_seconds: Math.round((Date.now() - formStartTimeRef.current) / 1000)
      });
      hasSubmittedRef.current = false; // Allow abandon tracking since submission failed
      // Check if Auth backend is running
      alert(`Registration failed! Is the Auth backend running on port 2102? Error: ${error}`);
      setErrors({ submit: 'Unable to connect to the server. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleFieldChange = (field: keyof RegisterFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // T_GA4_FIELD_INTERACTION: Track first interaction with each field
    if (!touchedFieldsRef.current.has(field)) {
      touchedFieldsRef.current.add(field);
      const timeToFieldSeconds = Math.round((Date.now() - formStartTimeRef.current) / 1000);
      trackGA4Event('registration_field_focus', {
        form_name: 'customer_registration',
        field_name: field,
        field_order: touchedFieldsRef.current.size,
        time_to_field_seconds: timeToFieldSeconds
      });
    }

    // Clear error for changed field
    if (errors[field]) {
      const clearedErrors = { ...errors };
      delete clearedErrors[field];
      setErrors(clearedErrors);
    }
  };
  
  const handleLoginClick = () => {
    navigate('/auth/sign-in');
  };

  /**
   * TRANSFORMATION (T): Resend verification email handler
   * Calls backend API to send a new verification email
   */
  const handleResendVerification = async () => {
    // T_RESEND_FALLBACK_2026_01_02: Use formData.email as fallback if registeredEmail is empty
    const emailToResend = registeredEmail || formData.email;

    if (!emailToResend) {
      setErrors({ submit: 'No email address found. Please enter your email and try again.' });
      return;
    }

    setIsLoading(true);
    try {
      const hostname = window.location.hostname;
      const isProduction = hostname === 'www.bitsbound.com' ||
                          hostname === 'bitsbound.com' ||
                          hostname.includes('vercel.app');
      const authBackendUrl = isProduction
        ? 'https://bitsbound-auth-backend.vercel.app'
        : 'http://localhost:2102';

      console.log('📧 Resending verification to:', emailToResend);
      const response = await fetch(`${authBackendUrl}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailToResend })
      });

      const data = await response.json();
      if (data.success) {
        // Show success message
        setErrors({ submit: '' });  // Clear any errors
        alert('Verification email sent! Please check your inbox.');
      } else {
        setErrors({ submit: data.error || 'Failed to resend verification email' });
      }
    } catch (error) {
      console.error('Resend verification error:', error);
      setErrors({ submit: 'Failed to resend verification email. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  // NOTE: Google OAuth removed from registration flow
  // Users must register with email/password first, then can link Google in account settings
  // Google OAuth is only available on the sign-in page for returning users

  // T_REGISTRATION_ENABLED_2026_01_04: Registration is now LIVE - SAC QB Draft Mode complete
  const REGISTRATION_DISABLED = false;

  if (REGISTRATION_DISABLED) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)',
        padding: '2rem'
      }}>
        <div style={{
          maxWidth: '500px',
          padding: '3rem',
          borderRadius: '16px',
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🚀</div>
          <h1 style={{ color: '#20b2aa', fontSize: '1.75rem', marginBottom: '1rem' }}>
            Coming Soon
          </h1>
          <p style={{ color: '#e0e0e0', fontSize: '1.1rem', lineHeight: 1.6, marginBottom: '1.5rem' }}>
            We're preparing exciting new features and contract verticals. Registration will reopen shortly.
          </p>
          <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: '2rem' }}>
            Already have an account?{' '}
            <a href="/auth/sign-in" style={{ color: '#20b2aa', textDecoration: 'none' }}>
              Sign in here
            </a>
          </p>
          <a
            href="/"
            style={{
              display: 'inline-block',
              padding: '0.75rem 2rem',
              background: 'linear-gradient(135deg, #20b2aa 0%, #1a9090 100%)',
              color: '#fff',
              borderRadius: '8px',
              textDecoration: 'none',
              fontWeight: 500
            }}
          >
            Return to Home
          </a>
        </div>
      </div>
    );
  }

  return (
    <Pea_Lobby_Register_SRC_Auth_Customer_Kings
      formData={formData}
      errors={errors}
      isLoading={isLoading}
      onFieldChange={handleFieldChange}
      onSubmit={handleSubmit}
      onLoginClick={handleLoginClick}
      // EMAIL VERIFICATION STATE
      verificationPending={verificationPending}
      registeredEmail={registeredEmail}
      onResendVerification={handleResendVerification}
      // NOTE: Google OAuth intentionally NOT passed for registration
      // Users must register with email/password first, then can link Google in account settings
    />
  );
};

export default Pod_Lobby_Register_SRC_Auth_Customer_Kings;